import { Link } from 'react-router-dom';
import { MicrosoftStoreButton, PageHero } from '../../components/Common';
import { ReleasePanel } from '../../components/ReleasePanel';
import { Seo } from '../../components/Seo';
import { track } from '../../components/analytics';
import { installWarnings, microsoftStore, webApp } from '../../data/site';

export function AndroidDownload() {
  return (
    <>
      <Seo
        title="Download the transport app for Android"
        breadcrumb="Download for Android"
        description="Download the latest official Transport Book APK."
      />
      <PageHero
        eyebrow="Android download"
        title="Transport Book for Android."
        description="Download the signed production APK from the official release service."
      />
      <ReleasePanel
        manifestUrl={import.meta.env.VITE_MOBILE_PRODUCTION_MANIFEST_URL}
        title="Android production"
        description="For customers using the production environment."
      />
      <InstallGuide signIn="Choose Install, open Transport Book, and sign in with the username and password from your free trial, or the account your organisation’s administrator created for you." />
    </>
  );
}

export function InstallGuide({ signIn }: { signIn: string }) {
  return (
    <section className="section surface">
      <div className="container prose">
        <h2>Install the APK</h2>
        <ol>
          <li>Download the APK on your Android device.</li>
          <li>
            Open it and allow installs from the current browser or file manager if Android asks.
          </li>
          <li>{signIn}</li>
        </ol>
        <h2>Updating</h2>
        <p>
          Compatible updates arrive through the application. When a native update is required,
          install the newest APK over the existing production app.
        </p>
        <h2>Troubleshooting</h2>
        <p>
          Confirm you selected the production build, have sufficient storage, and are updating an
          installation signed by the same official release key.
        </p>
      </div>
    </section>
  );
}

/** The Store listing offered ahead of the direct installers, because it is the
 * route that keeps itself current with no work from the customer. */
function StorePanel() {
  return (
    <section className="section compact">
      <div className="container">
        <article className="store-panel">
          <div>
            <div className="eyebrow">Recommended for Windows</div>
            <h2>Install from the Microsoft Store</h2>
            <p>
              Windows installs the signed package and keeps it updated for you, under the update
              settings you already control. No installer to run, and no release feed to trust.
            </p>
            <ul className="tick-list">
              <li>Signed and distributed by Microsoft</li>
              <li>Updates handled by Windows</li>
              <li>Installs for the current user without administrator rights</li>
            </ul>
          </div>
          <div className="store-panel-action">
            <MicrosoftStoreButton variant="badge" />
            <p className="package-meta">Product ID {microsoftStore.productId}</p>
          </div>
        </article>
      </div>
    </section>
  );
}

function WebAppPanel() {
  return (
    <section className="section compact">
      <div className="container">
        <article className="store-panel">
          <div>
            <div className="eyebrow">No install needed</div>
            <h2>Or open it in the browser</h2>
            <p>{webApp.tagline}</p>
            <ul className="tick-list">
              <li>The same screens, roles, and records as the desktop</li>
              <li>Always current — a reload is the update</li>
              <li>Needs a connection while you work; no offline mode</li>
            </ul>
          </div>
          <div className="store-panel-action">
            <a
              className="button primary"
              href={webApp.url}
              rel="noopener"
              onClick={() => track('open_web_app', { placement: 'web_store_panel' })}
            >
              Open the web app <span aria-hidden="true">↗</span>
            </a>
            <p className="package-meta">
              <Link to="/web">Desktop or browser?</Link>
            </p>
          </div>
        </article>
      </div>
    </section>
  );
}

export function DesktopDownload() {
  return (
    <>
      <Seo
        title="Download transport software for Windows and Mac"
        breadcrumb="Desktop downloads"
        description="Install Transport Book from the Microsoft Store, or download the Windows installer or macOS ARM64 package directly."
      />
      <PageHero
        eyebrow="Desktop downloads"
        title="Choose how you install."
        description="The Microsoft Store is the recommended route on Windows. Direct installers stay available for machines without Store access, alongside a manual ARM64 package for macOS. Or skip installing: the web app runs the same workspace in the browser."
      />
      <StorePanel />
      <WebAppPanel />
      <ReleasePanel
        manifestUrl={import.meta.env.VITE_DESKTOP_MANIFEST_URL}
        title="Direct downloads"
        description="For machines that cannot install from the Store. The recommended Windows installer is offered first, every package published for the current version is listed below it, and earlier versions still kept in the release store appear under Previous versions."
      />
      <section className="section surface">
        <div className="container grid two">
          <article className="card">
            <h2 className="card-title">Windows options</h2>
            <h3>Microsoft Store</h3>
            <p>
              Recommended. Windows installs and updates it for you, and the package is signed by
              Microsoft.
            </p>
            <h3>Installer (.exe)</h3>
            <p>
              For machines without Store access. It is the only direct Windows package the in-app
              updater serves, so it keeps itself current.
            </p>
            <h3>Windows SmartScreen</h3>
            <p>{installWarnings.windows}</p>
            <h3>Earlier versions</h3>
            <p>
              Every build still held in the release store is listed by version above, for staged
              rollouts and for pinning a machine to a known build.
            </p>
          </article>
          <article className="card">
            <h2 className="card-title">macOS ARM64</h2>
            <p>
              The current DMG is manual and not yet Developer ID signed or notarized. Verify its
              checksum and follow Gatekeeper guidance.
            </p>
            <h3>Which one updates itself?</h3>
            <p>
              The Store package and the Windows .exe installer both stay current on their own. The
              macOS DMG does not — plan to redeploy it.
            </p>
          </article>
        </div>
      </section>
    </>
  );
}

export function DriverDownload() {
  return (
    <>
      <Seo
        title="Driver app for truck drivers"
        breadcrumb="Driver app"
        description="Download the latest official Transport Book Driver APK for your drivers."
      />
      <PageHero
        eyebrow="Driver app download"
        title="Transport Book Driver for Android."
        description="The driver's app: their trip, photos, expenses and hesab, with live location while a trip is on. Download the signed production APK."
      />
      <ReleasePanel
        manifestUrl={import.meta.env.VITE_DRIVER_PRODUCTION_MANIFEST_URL}
        title="Driver app production"
        description="For drivers of organisations on the production environment. The office switches each driver on from the employee form."
      />
      <InstallGuide signIn="Choose Install, open Transport Book Driver, and sign in with your mobile number and the code sent to it by SMS. The office switches each driver on from their employee record first." />
    </>
  );
}
